import React, { useState, useEffect, useRef } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { getLeaseRecordsApiErrorMessage } from '../LeaseRecordsApiError';
import { Loader2, X, AlertTriangle, Save, Plus } from 'src/components/common/icons';
import { Field, SelectField } from './Master';

interface ILeaseCollectionHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    liTg: number;
}

interface ICollectionHistory {
    liTg: number;
    modDate: string;
    oldCollectionNo: string;
    newCollectionNo: string;
    reason: string;
    oldCollectionFlag: number;
    newCollectionFlag: number;
}

// DTO for the Dropdown source
interface ICollectionFlag {
    code: number;
    name: string;
}

interface IFlagOption {
    value: string;
    label: string;
}

const LeaseCollectionHistoryModal: React.FC<ILeaseCollectionHistoryModalProps> = ({ isOpen, onClose, onSave, liTg }) => {
    const { get, post, response } = useFetch();
    const isMounted = useRef(false);

    // --- State ---
    const [history, setHistory] = useState<ICollectionHistory[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Dropdown Data
    const [flagOptions, setFlagOptions] = useState<IFlagOption[]>([]);
    // Store raw flags for grid lookup
    const [rawFlags, setRawFlags] = useState<ICollectionFlag[]>([]);

    // Form State
    const [isCreating, setIsCreating] = useState(false);
    const [formData, setFormData] = useState<Partial<ICollectionHistory>>({});

    // --- Lifecycle ---
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isOpen && liTg) {
            loadData();
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, liTg]);

    // --- Actions ---

    const resetForm = () => {
        setHistory([]);
        setErrorMsg(null);
        setIsCreating(false);
        setFormData({});
    };

    const loadData = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            // 1. Fetch Flags (Lookup Table)
            // We fetch this first or in parallel so we can render the grid names correctly
            const flagsDataPromise = get('/v1.0/leases/master/collection-flags');

            // 2. Fetch History Data
            const histDataPromise = get(`/v1.0/leases/invoices/${liTg}/collection-history`);

            const [flagsData, histData] = await Promise.all([flagsDataPromise, histDataPromise]);

            if (isMounted.current) {
                if (response.ok) {
                    // Process Flags
                    if (Array.isArray(flagsData)) {
                        setRawFlags(flagsData);
                        const opts = flagsData.map((f: ICollectionFlag) => ({
                            value: f.code.toString(),
                            label: f.name, // Use the 'name' column from DB
                        }));
                        // Optional: Add a 'Select...' default if needed
                        opts.unshift({ value: '', label: 'Select...' });
                        setFlagOptions(opts);
                    }

                    // Process History
                    setHistory(histData || []);
                } else {
                    setErrorMsg('Failed to load data.');
                }
            }
        } catch (e) {
            if (isMounted.current) {
                setErrorMsg('System error while loading data.');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleCreateNew = () => {
        setIsCreating(true);
        // Initialize form
        setFormData({
            liTg: liTg,
            modDate: new Date().toISOString(),
            oldCollectionNo: '',
            newCollectionNo: '',
            reason: '',
            oldCollectionFlag: 0,
            newCollectionFlag: 0,
        });
        setErrorMsg(null);
    };

    const handleCancel = () => {
        setIsCreating(false);
        setFormData({});
        setErrorMsg(null);
    };

    const handleSave = async () => {
        // Basic Validation
        if (!formData.newCollectionNo) {
            setErrorMsg('New Collection No. is required.');
            return;
        }
        if (!formData.reason) {
            setErrorMsg('Reason is required.');
            return;
        }

        setLoading(true);
        setErrorMsg(null);

        try {
            // Ensure flags are integers (dropdown returns string)
            const payload = {
                ...formData,
                oldCollectionFlag: Number(formData.oldCollectionFlag || 0),
                newCollectionFlag: Number(formData.newCollectionFlag || 0),
            };

            await post('/v1.0/leases/invoices/collection-history', payload);
            onSave();

            if (isMounted.current) {
                if (response.ok) {
                    setIsCreating(false);
                    setFormData({});
                    // Reload to see changes
                    await loadData();
                } else {
                    const data = response.data;
                    setErrorMsg(getLeaseRecordsApiErrorMessage(data || response.data, response.status));
                }
            }
        } catch (e) {
            if (isMounted.current) {
                setErrorMsg('System error occurred during save.');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // Helper to get name from ID for the Grid
    const getFlagName = (code: number) => {
        const found = rawFlags.find((f) => f.code === code);
        return found ? found.name : code; // Fallback to code if name not found
    };

    // --- Render Helpers ---

    const renderForm = () => (
        <div style={styles.formContainer}>
            <div style={styles.formHeader}>
                <h4 style={styles.formTitle}>{isCreating ? 'Add New Entry' : 'Edit Entry'}</h4>
            </div>

            <div style={styles.row}>
                <div style={{ flex: 1 }}>
                    <Field
                        label="New Collection No"
                        value={formData.newCollectionNo || ''}
                        onChange={(v) => setFormData((prev) => ({ ...prev, newCollectionNo: v }))}
                        editable={true}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <SelectField
                        label="New Type"
                        value={formData.newCollectionFlag?.toString() || ''}
                        onChange={(v) => setFormData((prev) => ({ ...prev, newCollectionFlag: parseInt(v, 10) }))}
                        options={flagOptions}
                        editable={true}
                    />
                </div>
            </div>

            <div style={styles.row}>
                <div style={{ flex: 1 }}>
                    <Field
                        label="Reason"
                        value={formData.reason || ''}
                        onChange={(v) => setFormData((prev) => ({ ...prev, reason: v }))}
                        editable={true}
                    />
                </div>
            </div>

            <div style={styles.formActions}>
                <button onClick={handleCancel} style={styles.btnCancel} disabled={loading}>
                    Cancel
                </button>
                <button onClick={handleSave} style={styles.btnSave} disabled={loading}>
                    {loading ? <Loader2 style={styles.spinner} /> : <Save style={{ width: '14px', marginRight: '5px' }} />}
                    Save
                </button>
            </div>
        </div>
    );

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>Update Collection Number History</h2>
                    <button onClick={onClose} style={styles.closeButton} aria-label="Close">
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.modalBody}>
                    {errorMsg && (
                        <div style={styles.errorState}>
                            <AlertTriangle style={styles.errorIcon} />
                            {errorMsg}
                        </div>
                    )}

                    {/* Toolbar (Only visible when not editing) */}
                    {!isCreating && (
                        <div style={styles.toolbar}>
                            <button onClick={handleCreateNew} style={styles.btnAdd}>
                                <Plus style={{ width: '14px', marginRight: '5px' }} />
                                Add New Entry
                            </button>
                        </div>
                    )}

                    {/* Form Section */}
                    {isCreating && renderForm()}

                    {/* Data Table */}
                    <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                            <thead style={styles.tableHead}>
                                <tr>
                                    <th style={styles.tableHeaderCell}>Date Modified</th>
                                    <th style={styles.tableHeaderCell}>Old Coll. No</th>
                                    <th style={styles.tableHeaderCell}>Old Type</th>
                                    <th style={styles.tableHeaderCell}>New Coll. No</th>
                                    <th style={styles.tableHeaderCell}>New Type</th>
                                    <th style={styles.tableHeaderCell}>Reason</th>
                                </tr>
                            </thead>
                            <tbody style={styles.tableBody}>
                                {history.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={styles.emptyState}>
                                            No collection history found.
                                        </td>
                                    </tr>
                                ) : (
                                    history.map((h, idx) => (
                                        <tr key={`${h.liTg}-${h.modDate}-${idx}`} style={styles.tableRow}>
                                            <td style={styles.tableDataCell}>{new Date(h.modDate).toLocaleDateString()}</td>
                                            <td style={styles.tableDataCell}>{h.oldCollectionNo || '-'}</td>
                                            {/* Look up the name from rawFlags using the ID */}
                                            <td style={styles.tableDataCell}>{getFlagName(h.oldCollectionFlag)}</td>
                                            <td style={styles.tableDataCell}>{h.newCollectionNo || '-'}</td>
                                            <td style={styles.tableDataCell}>{getFlagName(h.newCollectionFlag)}</td>
                                            <td style={styles.tableDataCell}>{h.reason}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        backdropFilter: 'blur(2px)',
    },
    modalContent: {
        backgroundColor: '#fff',
        borderRadius: '0.75rem',
        width: '95%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        fontFamily: 'inherit',
    },
    modalHeader: {
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: '0.75rem 0.75rem 0 0',
    },
    modalTitle: { margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#111827' },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },

    modalBody: { padding: '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', flexGrow: 1, backgroundColor: '#F9FAFB' },

    toolbar: { display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' },
    btnAdd: {
        padding: '0.5rem 1rem',
        backgroundColor: '#4F46E5',
        color: '#fff',
        border: 'none',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.875rem',
        fontWeight: 500,
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'background-color 0.15s ease-in-out',
    },

    formContainer: {
        padding: '1.25rem',
        backgroundColor: '#FFFFFF',
        borderRadius: '0.5rem',
        border: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        marginBottom: '1.5rem',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    },
    formHeader: { marginBottom: '0.5rem', borderBottom: '1px solid #F3F4F6', paddingBottom: '0.5rem' },
    formTitle: { margin: 0, fontSize: '1rem', fontWeight: 600, color: '#374151' },
    row: { display: 'flex', gap: '1.5rem' },

    formActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
    btnSave: {
        padding: '0.5rem 1rem',
        backgroundColor: '#10B981',
        color: '#fff',
        border: 'none',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    btnCancel: {
        padding: '0.5rem 1rem',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        border: '1px solid #D1D5DB',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        fontSize: '0.875rem',
        fontWeight: 500,
    },

    tableWrapper: {
        border: '1px solid #E5E7EB',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
    tableHead: { backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' },
    tableHeaderCell: {
        padding: '0.75rem 1rem',
        textAlign: 'left',
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.05em',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    tableRow: { borderBottom: '1px solid #F3F4F6', transition: 'background-color 0.1s' },
    tableDataCell: { padding: '0.75rem 1rem', color: '#1F2937', verticalAlign: 'middle' },
    emptyState: { textAlign: 'center', padding: '3rem', color: '#9CA3AF', fontStyle: 'italic' },

    actionIcons: { display: 'flex', gap: '0.5rem', justifyContent: 'center' },
    iconBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#4F46E5',
        padding: '0.25rem',
        borderRadius: '0.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 0.15s',
    },
    iconBtnDelete: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#EF4444',
        padding: '0.25rem',
        borderRadius: '0.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 0.15s',
    },

    errorState: {
        display: 'flex',
        alignItems: 'center',
        padding: '1rem',
        marginBottom: '1rem',
        color: '#B91C1C',
        fontWeight: 500,
        backgroundColor: '#FEE2E2',
        borderRadius: '0.5rem',
        border: '1px solid #FECACA',
    },
    errorIcon: { width: '1.25rem', height: '1.25rem', marginRight: '0.75rem' },
    spinner: { width: '0.9rem', height: '0.9rem', animation: 'spin 1s linear infinite', color: '#FFFFFF', marginRight: '4px' },
};

export default LeaseCollectionHistoryModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LeaseCollectionHistoryModal.tsx